import React, {useEffect, useMemo, useRef, useState} from 'react';
import {BaseModal} from 'components/modals';
import Button from '@/components/ui/Button';
import TableComponent from '@/components/table/Table';
import {ColumnDef} from '@tanstack/react-table';
import {PivotMatrixData} from '@/views/storyboard/charts/utils/pivotCellsToMatrix';
import {useStoreActions} from 'store';
import i18n from 'i18n';

interface Props {
    width: number;
    height: number;
    matrix: PivotMatrixData;
    rowHeader: string;
    chartId: number | null;
    chartTitle: string;
}

const PivotTableContent = React.memo<Props>((
    {
        width,
        height,
        matrix,
        rowHeader,
        chartId,
        chartTitle,
    },
) => {
    const innerContentRef = useRef<HTMLDivElement>(null);
    const [showMoreButtonVisible, setShowMoreButtonVisible] = useState(false);
    const [showMoreModalOpen, setShowMoreModalOpen] = useState(false);

    const tableData = useMemo(() => matrix.rows.map((row) => ({
        rowKey: row.rowKey,
        ...row.values,
        rowTotal: row.rowTotal,
    })), [matrix.rows]);

    const columns: ColumnDef<Record<string, string | number>>[] = useMemo(() => [
        {
            id: 'rowKey',
            header: rowHeader,
            accessorKey: 'rowKey',
        },
        ...matrix.columns.map((columnKey) => ({
            id: columnKey,
            header: columnKey,
            accessorKey: columnKey,
            cell: ({getValue}: {getValue: () => unknown}) => {
                const value = getValue();
                return value === '' ? '—' : value;
            },
        })),
        {
            id: 'rowTotal',
            header: i18n.t('pivot_row_total.text'),
            accessorKey: 'rowTotal',
        },
    ], [matrix.columns, rowHeader]);

    const table = <TableComponent data={tableData} columns={columns} sortable tableClassName="storyboard-chart-table" />;

    const offsetHeight = innerContentRef.current?.offsetHeight;

    useEffect(() => {
        if (offsetHeight && offsetHeight > height) {
            setShowMoreButtonVisible(true);
        } else {
            setShowMoreButtonVisible(false);
        }
    }, [offsetHeight, tableData, width, height]);

    const storeCSVDownloadCallback = useStoreActions((actions) => actions.tableChartDownloadCallbacks.setCallback);
    const removeCSVDownloadCallback = useStoreActions((actions) => actions.tableChartDownloadCallbacks.removeCallback);

    useEffect(() => {
        if (!chartId) {
            return undefined;
        }

        const csvData = [
            columns.map((col) => String(col.header ?? '')).join(','),
            ...tableData.map((row) => columns.map((col) => {
                const key = 'accessorKey' in col ? String(col.accessorKey) : col.id ?? '';
                return row[key as keyof typeof row] ?? '';
            }).join(',')),
        ].join('\n');

        storeCSVDownloadCallback({
            [chartId]: () => {
                const blob = new Blob([csvData], {type: 'text/csv'});
                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = `${chartTitle}.csv`;
                anchor.click();
            },
        });

        return () => removeCSVDownloadCallback(chartId);
    }, [chartId, chartTitle, tableData, columns, storeCSVDownloadCallback, removeCSVDownloadCallback]);

    return (
        <div
            className="px-2 comment-wrapper position-relative overflow-hidden"
            style={{
                width,
                height: height - 16,
            }}
        >
            <div className="top-scrollbar-outer">
                <div className="top-scrollbar-inner" />
            </div>
            <div
                className="chart-table-container table-responsive position-relative overflow-scroll"
                style={{
                    display: 'grid',
                    position: 'absolute',
                    width: '100%',
                }}
            >
                <div ref={innerContentRef}>
                    {table}
                </div>
            </div>
            {showMoreButtonVisible ? (
                <div className="show-more-button position-absolute w-100 pl-3">
                    <Button
                        variant="link"
                        size="sm"
                        className="plain-button position-absolute text-info"
                        onClick={() => setShowMoreModalOpen(true)}
                    >
                        {i18n.t('jt_show_all.text')}
                    </Button>
                </div>
            ) : null}

            <BaseModal
                isOpen={showMoreModalOpen}
                onClose={() => setShowMoreModalOpen(false)}
                title={chartTitle}
                size="xl"
                dialogClassName="fixed-height-modal modal-80w"
            >
                <div className="fixed-header-table-wrapper">
                    {table}
                </div>
            </BaseModal>
        </div>
    );
});

export default PivotTableContent;
